import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { formatDate, formatDateShort, formatDateTime } from "@/lib/dateFormat";
import { useI18n } from "@/context/I18nContext";
import { useHelp } from "@/context/HelpContext";
import { toast } from "sonner";
import { BlogAdmin } from "@/pages/Blog";
import { BudgetWidget } from "@/pages/SuperAdmin";
import WeatherWidget from "@/components/WeatherWidget";
import QuotaCalendar from "@/components/QuotaCalendar";
import MessagesPanel from "@/components/MessagesPanel";
import LargeConsumerModule from "@/components/LargeConsumerModule";
import ApprovalDialog from "@/components/ApprovalDialog";
import { ForecastChart } from "@/components/ForecastChart";
import { PendingUsersPanel } from "@/components/PendingUsersPanel";
import { RegistrationSettings } from "@/components/RegistrationSettings";
import ProfilePanel from "@/components/ProfilePanel";
import { Droplets, MapPin, Users, TrendingUp, CheckCircle2, XCircle, Mail, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart, Line, Legend } from "recharts";

export default function MunicipalityDashboard() {
  const { t } = useI18n();
  const { setHelpKey } = useHelp();
  const [muni, setMuni] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [today, setToday] = useState(null);
  const [range, setRange] = useState({ quota_m3: 0, usage: {} });
  const [forecast, setForecast] = useState(null);
  const [tab, setTab] = useState("overview");
  const [approving, setApproving] = useState(null); // { ids, preview, action, recipients }

  const todayStr = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    setHelpKey(`muni.${tab}`);
    return () => setHelpKey(null);
  }, [tab, setHelpKey]);

  const loadAll = async () => {
    const [m, b, tAvail, r, f] = await Promise.all([
      api.get("/municipalities/mine"),
      api.get("/bookings"),
      api.get(`/quota/availability?day=${todayStr}`),
      api.get(`/quota/range?start=${todayStr}&end=${new Date(Date.now() + 13 * 864e5).toISOString().slice(0, 10)}`),
      api.get("/forecast?days=30"),
    ]);
    setMuni(m.data);
    setBookings(b.data);
    setToday(tAvail.data);
    setRange(r.data);
    setForecast(f.data);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const updateMuni = async (patch) => {
    const r = await api.patch("/municipalities/mine", patch);
    setMuni(r.data);
    toast.success(t("save") + " ✓");
    (r.data.postal_area_conflicts || []).forEach((c) =>
      toast.warning(t("postal_area_conflict").replace("{area}", `${c.plz} ${c.city}`).replace("{muni}", c.municipality)),
    );
    // reload quota
    const tAvail = await api.get(`/quota/availability?day=${todayStr}`);
    setToday(tAvail.data);
  };

  const setBookingStatus = async (id, status, approvalMessage) => {
    if (status === "__reload__") {
      await loadAll();
      return;
    }
    const body = { status };
    if (approvalMessage !== undefined) body.approval_message = approvalMessage;
    await api.patch(`/bookings/${id}`, body);
    loadAll();
  };

  const confirmApproval = async (message) => {
    if (!approving?.ids?.length) return;
    const ids = approving.ids;
    const count = ids.length;
    const isBulk = count > 1;
    const action = approving.action || "approve";
    const targetStatus = action === "reject" ? "rejected" : "approved";
    const doneLabel = action === "reject" ? t("reject") : t("approve");
    // Close the dialog immediately; single actions fire now, bulk approvals get a 10s undo window.
    setApproving(null);
    if (!isBulk || action === "reject") {
      // Bulk-rejects fire immediately (no undo grace) — rejections are not sent
      // as bulk mailshots by default, and the operator has already confirmed.
      const body = { ids, status: targetStatus };
      if (message) body.approval_message = message;
      await api.post("/bookings/bulk", body);
      toast.success(`${count} · ${doneLabel} ✓`);
      await loadAll();
      return;
    }
    // --- Bulk approve with 10s undo grace ---
    let cancelled = false;
    let remaining = 10;
    const toastId = `bulk-approve-${Date.now()}`;
    const fire = async () => {
      if (cancelled) return;
      await api.post("/bookings/bulk", { ids, status: "approved", approval_message: message });
      toast.dismiss(toastId);
      toast.success(`${count} · ${t("approve")} ✓`);
      await loadAll();
    };
    const render = () =>
      toast(
        t("bulk_pending_send")
          .replace("{count}", String(count))
          .replace("{seconds}", String(remaining)),
        {
          id: toastId,
          duration: Infinity,
          action: {
            label: t("undo"),
            onClick: () => {
              cancelled = true;
              clearInterval(tick);
              toast.dismiss(toastId);
              toast.info(t("bulk_undone"));
            },
          },
          cancel: {
            label: t("bulk_send_now"),
            onClick: () => {
              clearInterval(tick);
              fire();
            },
          },
          "data-testid": "bulk-undo-toast",
        },
      );
    render();
    const tick = setInterval(() => {
      remaining -= 1;
      if (cancelled) return;
      if (remaining <= 0) {
        clearInterval(tick);
        fire();
        return;
      }
      render();
    }, 1000);
  };

  const openSingleApprove = (booking) =>
    setApproving({
      ids: [booking.id],
      preview: booking,
      action: "approve",
      recipients: [booking],
    });
  const openBulkApprove = (ids, recipients) =>
    setApproving({ ids, preview: null, action: "approve", recipients: recipients || [] });
  const openBulkReject = (ids, recipients) =>
    setApproving({ ids, preview: null, action: "reject", recipients: recipients || [] });

  const chartData = Object.entries(range.usage || {})
    .sort()
    .map(([d, v]) => ({ d: formatDateShort(d), used: v, quota: range.quota_m3 }));

  const uniqueOwners = new Set(bookings.map((b) => b.user_id)).size;

  return (
    <div className="space-y-6">
      {muni && (
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <div className="label-caps">{t("municipality")}</div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{muni.name}</h1>
          </div>
        </div>
      )}

      {/* Bento KPI grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Kpi
          icon={Droplets}
          label={t("available_today")}
          value={today ? `${today.available_m3}` : "—"}
          unit="m³"
          testId="kpi-available"
          accent
        />
        <Kpi
          icon={TrendingUp}
          label={t("used_today")}
          value={today ? `${today.used_m3}` : "—"}
          unit={`/ ${today?.quota_m3 ?? "—"} m³`}
          testId="kpi-used"
        />
        <Kpi
          icon={Users}
          label={t("citizens")}
          value={muni ? String(muni.citizen_count ?? uniqueOwners) : "—"}
          unit=""
          testId="kpi-users"
        />
        <Kpi
          icon={MapPin}
          label={t("bookings")}
          value={String(bookings.length)}
          unit=""
          testId="kpi-bookings"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="muni-tabs" className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">{t("dashboard")}</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">{t("all_bookings")}</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="tab-forecast">{t("forecast")}</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">{t("calendar")}</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">{t("messages")}</TabsTrigger>
          <TabsTrigger value="large" data-testid="tab-large">{t("large_consumer")}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">{t("settings")}</TabsTrigger>
          <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">Profil</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <PendingUsersPanel onChanged={loadAll} />
          <BudgetWidget />
          <WeatherWidget liveWeatherEnabled={!!muni?.feature_flags?.live_weather} />
          <Card className="border-border">
            <CardContent className="p-6">
              <h3 className="mb-4 text-lg font-semibold">{t("quota_usage")} (14d)</h3>
              {chartData.length === 0 ? (
                <div className="grid h-64 place-items-center text-sm text-muted-foreground">{t("no_data")}</div>
              ) : (
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={chartData}>
                      <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                      <XAxis dataKey="d" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                      <Bar dataKey="used" fill="#00A6FB" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <RecentBookings bookings={bookings.slice(0, 5)} t={t} onStatus={setBookingStatus} onApprove={openSingleApprove} />
        </TabsContent>

        <TabsContent value="bookings">
          <BookingsTable bookings={bookings} t={t} onStatus={setBookingStatus} onApprove={openSingleApprove} onBulkApprove={openBulkApprove} onBulkReject={openBulkReject} />
        </TabsContent>

        <TabsContent value="forecast">
          <ForecastCard forecast={forecast} t={t} />
        </TabsContent>

        <TabsContent value="calendar">
          <QuotaCalendar showOverbooking />
        </TabsContent>

        <TabsContent value="messages">
          <MessagesPanel />
        </TabsContent>

        <TabsContent value="large">
          <LargeConsumerModule />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          {muni && <RegistrationSettings muni={muni} onSave={updateMuni} t={t} />}
          {muni && <MunicipalitySettings muni={muni} onSave={updateMuni} t={t} />}
        </TabsContent>

        <TabsContent value="blog">
          <BlogAdmin />
        </TabsContent>

        <TabsContent value="profile">
          <ProfilePanel />
        </TabsContent>
      </Tabs>

      <ApprovalDialog
        open={!!approving}
        booking={approving?.preview || null}
        bulkCount={approving?.ids?.length || 0}
        bulkRecipients={approving?.recipients || []}
        action={approving?.action || "approve"}
        municipality={muni}
        onCancel={() => setApproving(null)}
        onConfirm={confirmApproval}
      />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, unit, testId, accent }) {
  return (
    <Card className={`border-border ${accent ? "border-primary/40" : ""}`}>
      <CardContent className="p-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="label-caps">{label}</div>
          <Icon className={`h-4 w-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
        </div>
        <div className="flex items-baseline gap-2" data-testid={testId}>
          <span className={`font-mono-jb text-3xl font-semibold ${accent ? "text-primary" : ""}`}>{value}</span>
          <span className="text-sm text-muted-foreground">{unit}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function ForecastCard({ forecast, t }) {
  // Weekly roll-up (chunks of 7 from today)
  const weeks = [];
  const series = forecast?.series || [];
  for (let i = 0; i < series.length; i += 7) {
    const chunk = series.slice(i, i + 7);
    if (chunk.length === 0) break;
    const total = chunk.reduce((s, r) => s + (r.projected_m3 || 0), 0);
    const booked = chunk.reduce((s, r) => s + (r.booked_m3 || 0), 0);
    const capacity = chunk.reduce((s, r) => s + (r.quota_m3 || 0), 0);
    weeks.push({
      idx: i / 7 + 1,
      from: chunk[0].date,
      to: chunk[chunk.length - 1].date,
      total: Math.round(total * 10) / 10,
      booked: Math.round(booked * 10) / 10,
      capacity: Math.round(capacity * 10) / 10,
      pct: capacity > 0 ? Math.min(100, Math.round((total / capacity) * 100)) : 0,
    });
  }

  const downloadCsv = () => {
    if (weeks.length === 0) return;
    const header = [
      t("csv_col_week"),
      t("csv_col_from"),
      t("csv_col_to"),
      t("csv_col_projected"),
      t("csv_col_booked"),
      t("csv_col_capacity"),
      t("csv_col_pct"),
    ];
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = weeks.map((w) => [w.idx, w.from, w.to, w.total, w.booked, w.capacity, w.pct]);
    const csv = [header, ...rows].map((r) => r.map(escape).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `water-planner-forecast-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {weeks.length > 0 && (
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold">{t("weekly_rollup")}</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadCsv}
                data-testid="forecast-download-csv-btn"
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                {t("download_csv")}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {weeks.map((w) => {
                const heat =
                  w.pct >= 90
                    ? "border-destructive/60 text-destructive"
                    : w.pct >= 60
                      ? "border-yellow-500/60 text-yellow-700 dark:text-yellow-400"
                      : "border-primary/40 text-primary";
                return (
                  <div
                    key={w.idx}
                    className={`rounded-md border p-4 ${heat}`}
                    data-testid={`week-rollup-${w.idx}`}
                  >
                    <div className="label-caps">
                      {t("week")} {w.idx} · {formatDateShort(w.from)} – {formatDateShort(w.to)}
                    </div>
                    <div className="mt-2 font-mono-jb text-2xl font-semibold">
                      {w.total} m³
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {w.pct}% / {w.capacity} m³
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="mb-1 text-lg font-semibold">{t("forecast")} (30d)</h3>
          <p className="mb-4 text-xs text-muted-foreground">{t("forecast_all_bookings_hint")}</p>
          <ForecastChart series={series} t={t} testId="muni-forecast-chart" />
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    pending: "outline",
    approved: "default",
    rejected: "destructive",
    completed: "secondary",
  };
  const hint = status === "completed" ? t("status_completed_hint") : undefined;
  return (
    <Badge
      variant={map[status] || "outline"}
      title={hint}
      data-testid={`status-badge-${status}`}
      className={status === "completed" ? "gap-1" : undefined}
    >
      {status === "completed" && <CheckCircle2 className="h-3 w-3" />}
      {t(status)}
    </Badge>
  );
}

function BookingsTable({ bookings, t, onStatus, onApprove, onBulkApprove, onBulkReject }) {
  const [selected, setSelected] = React.useState(() => new Set());
  const [sortBy, setSortBy] = React.useState("date_desc");
  const [filterStatus, setFilterStatus] = React.useState("all");

  const pendingIds = React.useMemo(
    () => bookings.filter((b) => b.status === "pending").map((b) => b.id),
    [bookings],
  );

  React.useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => pendingIds.includes(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [pendingIds]);

  const rows = React.useMemo(() => {
    const filtered =
      filterStatus === "all"
        ? bookings.slice()
        : bookings.filter((b) => b.status === filterStatus);
    const cmp = {
      date_desc: (a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0),
      date_asc: (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0),
      name: (a, b) => (a.user_name || "").localeCompare(b.user_name || ""),
      status: (a, b) => {
        const order = { pending: 0, approved: 1, rejected: 2, completed: 3 };
        return (order[a.status] ?? 9) - (order[b.status] ?? 9);
      },
    };
    return filtered.sort(cmp[sortBy] || cmp.date_desc);
  }, [bookings, sortBy, filterStatus]);

  const selectedRecipients = React.useMemo(
    () => bookings.filter((b) => selected.has(b.id)),
    [bookings, selected],
  );

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === pendingIds.length && pendingIds.length > 0
        ? new Set()
        : new Set(pendingIds),
    );
  };

  const bulkReject = () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setSelected(new Set());
    onBulkReject(ids, selectedRecipients);
  };

  const bulkApprove = () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setSelected(new Set());
    onBulkApprove(ids, selectedRecipients);
  };

  const allChecked =
    pendingIds.length > 0 && selected.size === pendingIds.length;

  return (
    <Card className="border-border">
      <CardContent className="p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold">{t("all_bookings")}</h3>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
              data-testid="filter-status-select"
              aria-label={t("filter_status")}
            >
              <option value="all">{t("filter_all")}</option>
              <option value="pending">{t("pending")}</option>
              <option value="approved">{t("approved")}</option>
              <option value="rejected">{t("rejected")}</option>
              <option value="completed">{t("completed")}</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
              data-testid="sort-by-select"
              aria-label={t("sort_by")}
            >
              <option value="date_desc">{t("sort_date_desc")}</option>
              <option value="date_asc">{t("sort_date_asc")}</option>
              <option value="name">{t("sort_name")}</option>
              <option value="status">{t("sort_status")}</option>
            </select>
            {selected.size > 0 && (
              <div className="flex items-center gap-2" data-testid="bulk-bar">
                <span className="label-caps">
                  {selected.size} {t("selected")}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={bulkApprove}
                  data-testid="bulk-approve-btn"
                >
                  <CheckCircle2 className="mr-1 h-4 w-4" /> {t("bulk_approve")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={bulkReject}
                  data-testid="bulk-reject-btn"
                >
                  <XCircle className="mr-1 h-4 w-4" /> {t("bulk_reject")}
                </Button>
              </div>
            )}
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t("no_bookings")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left label-caps">
                  <th className="py-2 pr-3 w-8">
                    <Checkbox
                      checked={allChecked}
                      onCheckedChange={toggleAll}
                      disabled={pendingIds.length === 0}
                      data-testid="bulk-select-all"
                    />
                  </th>
                  <th className="py-2 pr-3">{t("date")}</th>
                  <th className="py-2 pr-3">{t("name")}</th>
                  <th className="py-2 pr-3">{t("address")}</th>
                  <th className="py-2 pr-3">{t("volume_m3")}</th>
                  <th className="py-2 pr-3">{t("status")}</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.id} className="border-b border-border/60">
                    <td className="py-2 pr-3">
                      {b.status === "pending" ? (
                        <Checkbox
                          checked={selected.has(b.id)}
                          onCheckedChange={() => toggle(b.id)}
                          data-testid={`row-select-${b.id}`}
                        />
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 font-mono-jb" title={b.date}>{formatDate(b.date)}</td>
                    <td className="py-2 pr-3">{b.user_name}</td>
                    <td className="py-2 pr-3 max-w-[240px] truncate text-muted-foreground">{b.user_address || "—"}</td>
                    <td className="py-2 pr-3 font-mono-jb">{b.volume_m3}</td>
                    <td className="py-2 pr-3"><StatusBadge status={b.status} t={t} /></td>
                    <td className="py-2 pr-3">
                      {b.status === "pending" ? (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => onApprove(b)} data-testid={`tbl-approve-${b.id}`}>
                            {t("approve")}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => onStatus(b.id, "rejected")} data-testid={`tbl-reject-${b.id}`}>
                            {t("reject")}
                          </Button>
                        </div>
                      ) : b.status === "approved" ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => onStatus(b.id, "completed")}
                          data-testid={`tbl-complete-${b.id}`}
                          title={t("status_completed_hint")}
                        >
                          {t("mark_as_done")}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecentBookings({ bookings, t, onStatus, onApprove }) {
  return (
    <Card className="border-border">
      <CardContent className="p-6">
        <h3 className="mb-4 text-lg font-semibold">{t("upcoming")}</h3>
        {bookings.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t("no_bookings")}</div>
        ) : (
          <ul className="divide-y divide-border">
            {bookings.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-[110px] font-mono-jb text-sm" title={b.date}>{formatDate(b.date)}</div>
                <div className="flex-1">
                  <div className="font-medium">{b.user_name}</div>
                  <div className="text-xs text-muted-foreground">{b.user_email}</div>
                </div>
                <div className="font-mono-jb text-sm">{b.volume_m3} m³</div>
                <StatusBadge status={b.status} t={t} />
                {b.status === "pending" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => onApprove(b)} data-testid={`approve-${b.id}`}>
                      <CheckCircle2 className="mr-1 h-4 w-4" /> {t("approve")}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onStatus(b.id, "rejected")} data-testid={`reject-${b.id}`}>
                      <XCircle className="mr-1 h-4 w-4" /> {t("reject")}
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function MunicipalitySettings({ muni, onSave, t }) {
  const [quota, setQuota] = useState(String(muni.daily_quota_m3));
  const [refill, setRefill] = useState(!!muni.refill_reporting_required);
  const [contactEmail, setContactEmail] = useState(muni.contact_email || "");
  const [tplDe, setTplDe] = useState(muni.approval_message_de || "");
  const [tplEn, setTplEn] = useState(muni.approval_message_en || "");
  const [sigDe, setSigDe] = useState(muni.email_signature_de || "");
  const [sigEn, setSigEn] = useState(muni.email_signature_en || "");
  const [sigApprove, setSigApprove] = useState(muni.signature_on_approval !== false);
  const [sigReject, setSigReject] = useState(muni.signature_on_rejection !== false);
  const [sigDone, setSigDone] = useState(muni.signature_on_completion !== false);
  const [previewLang, setPreviewLang] = useState("de");

  return (
    <div className="space-y-4">
      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="mb-4 text-lg font-semibold">{t("settings")}</h3>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <Label>{t("quota_m3")}</Label>
              <div className="flex gap-2">
                <Input type="number" step="1" min="0" value={quota} onChange={(e) => setQuota(e.target.value)} data-testid="settings-quota-input" />
                <Button onClick={() => onSave({ daily_quota_m3: parseFloat(quota) })} data-testid="save-quota-btn">
                  {t("save")}
                </Button>
              </div>
            </div>
            <div>
              <Label>{t("refill_report_required")}</Label>
              <div className="mt-2 flex items-center gap-3">
                <Switch checked={refill} onCheckedChange={(v) => { setRefill(v); onSave({ refill_reporting_required: v }); }} data-testid="refill-switch" />
                <span className="text-sm text-muted-foreground">{t("refill_report_required_hint")}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6">
          <div className="mb-4 flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">{t("email_settings")}</h3>
          </div>
          <div className="space-y-4">
            <div>
              <Label>{t("contact_email")}</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="rathaus@musterstadt.example"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  data-testid="contact-email-input"
                />
                <Button
                  onClick={() => onSave({ contact_email: contactEmail })}
                  data-testid="save-contact-email-btn"
                >
                  {t("save")}
                </Button>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{t("contact_email_hint")}</div>
            </div>

            <div>
              <Label>{t("template_de")}</Label>
              <Textarea
                rows={6}
                value={tplDe}
                onChange={(e) => setTplDe(e.target.value)}
                className="font-mono-jb text-sm"
                data-testid="template-de-input"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onSave({ approval_message_de: tplDe })}
                  data-testid="save-template-de-btn"
                >
                  {t("save")}
                </Button>
              </div>
            </div>

            <div>
              <Label>{t("template_en")}</Label>
              <Textarea
                rows={6}
                value={tplEn}
                onChange={(e) => setTplEn(e.target.value)}
                className="font-mono-jb text-sm"
                data-testid="template-en-input"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onSave({ approval_message_en: tplEn })}
                  data-testid="save-template-en-btn"
                >
                  {t("save")}
                </Button>
              </div>
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              {t("placeholders_hint")}
            </div>

            <div>
              <Label>{t("signature_de")}</Label>
              <Textarea
                rows={4}
                value={sigDe}
                onChange={(e) => setSigDe(e.target.value)}
                className="font-mono-jb text-sm"
                data-testid="signature-de-input"
                placeholder="Gemeindeverwaltung Musterstadt&#10;Rathausplatz 1&#10;wasser@musterstadt.example"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onSave({ email_signature_de: sigDe })}
                  data-testid="save-signature-de-btn"
                >
                  {t("save")}
                </Button>
              </div>
            </div>

            <div>
              <Label>{t("signature_en")}</Label>
              <Textarea
                rows={4}
                value={sigEn}
                onChange={(e) => setSigEn(e.target.value)}
                className="font-mono-jb text-sm"
                data-testid="signature-en-input"
                placeholder="Musterstadt Town Hall&#10;Rathausplatz 1&#10;water@musterstadt.example"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onSave({ email_signature_en: sigEn })}
                  data-testid="save-signature-en-btn"
                >
                  {t("save")}
                </Button>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{t("signature_hint")}</div>
            </div>

            <div>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const r = await api.post("/municipalities/test-email");
                    toast.success(`${t("test_email_sent")} · ${r.data.sent_to}`);
                  } catch (e) {
                    toast.error(
                      e.response?.data?.detail || t("test_email_error"),
                    );
                  }
                }}
                data-testid="test-email-btn"
                className="gap-2"
              >
                <Mail className="h-4 w-4" />
                {t("test_email")}
              </Button>
            </div>

            {(sigDe || sigEn) && (
              <div>
                <div className="flex items-center justify-between">
                  <Label>{t("signature_preview")}</Label>
                  <div
                    className="inline-flex overflow-hidden rounded-md border border-border text-xs"
                    data-testid="signature-preview-lang-toggle"
                  >
                    <button
                      type="button"
                      onClick={() => setPreviewLang("de")}
                      className={`px-2 py-1 ${previewLang === "de" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:bg-muted"}`}
                      data-testid="signature-preview-lang-de"
                    >
                      {t("preview_lang_de")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewLang("en")}
                      className={`px-2 py-1 ${previewLang === "en" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:bg-muted"}`}
                      data-testid="signature-preview-lang-en"
                    >
                      {t("preview_lang_en")}
                    </button>
                  </div>
                </div>
                <div
                  className="mt-2 rounded-md border border-border bg-muted/30 p-4 text-sm"
                  data-testid="signature-preview"
                >
                  <div className="border-t border-border/60 pt-3 text-muted-foreground">
                    {((previewLang === "de" ? sigDe : sigEn) || "")
                      .split(/\n\n+/)
                      .map((para, i) => (
                        <p key={i} className="mb-2 last:mb-0">
                          {para.split("\n").map((line, j, arr) => (
                            <React.Fragment key={j}>
                              {line}
                              {j < arr.length - 1 && <br />}
                            </React.Fragment>
                          ))}
                        </p>
                      ))}
                    {!(previewLang === "de" ? sigDe : sigEn) && (
                      <span className="text-xs italic" data-testid="signature-preview-empty">
                        {t("no_data")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <ToggleRow
                label={t("signature_on_approval")}
                checked={sigApprove}
                onChange={(v) => {
                  setSigApprove(v);
                  onSave({ signature_on_approval: v });
                }}
                testId="sig-approve-switch"
              />
              <ToggleRow
                label={t("signature_on_rejection")}
                checked={sigReject}
                onChange={(v) => {
                  setSigReject(v);
                  onSave({ signature_on_rejection: v });
                }}
                testId="sig-reject-switch"
              />
              <ToggleRow
                label={t("signature_on_completion")}
                checked={sigDone}
                onChange={(v) => {
                  setSigDone(v);
                  onSave({ signature_on_completion: v });
                }}
                testId="sig-done-switch"
              />
            </div>

            <PendingUtilitiesPanel t={t} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PendingUtilitiesPanel({ t }) {
  const [items, setItems] = React.useState([]);
  const [busyId, setBusyId] = React.useState(null);
  const [muniId, setMuniId] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const [meRes, listRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/admin/utilities/pending"),
      ]);
      setMuniId(meRes.data?.municipality_id || null);
      setItems(listRes.data || []);
    } catch {
      /* ignore */
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const decide = async (userId, action) => {
    if (!muniId) return;
    setBusyId(userId);
    try {
      await api.post(`/admin/utilities/${userId}/${action}`, {
        municipality_id: muniId,
      });
      toast.success(
        action === "approve" ? t("approve_utility_toast") : t("reject_utility_toast"),
      );
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="rounded-md border border-border p-4"
      data-testid="pending-utilities-panel"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="label-caps">{t("pending_utilities")}</div>
        <span className="font-mono-jb text-xs text-muted-foreground">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div
          className="rounded-md border border-dashed border-border/70 p-4 text-center text-sm text-muted-foreground"
          data-testid="pending-utilities-empty"
        >
          {t("pending_utilities_empty")}
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((u) => (
            <li
              key={u.id}
              className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3"
              data-testid={`pending-utility-${u.id}`}
            >
              <div className="flex-1">
                <div className="text-sm font-semibold">{u.name}</div>
                <div className="text-xs text-muted-foreground">{u.email}</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => decide(u.id, "approve")}
                disabled={busyId === u.id || !muniId}
                data-testid={`approve-utility-${u.id}`}
              >
                {t("approve_utility")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => decide(u.id, "reject")}
                disabled={busyId === u.id || !muniId}
                data-testid={`reject-utility-${u.id}`}
              >
                {t("reject_utility")}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}



function ToggleRow({ label, checked, onChange, testId }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border p-3">
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
      <span className="text-sm">{label}</span>
    </div>
  );
}
